import React from 'react';
import BaseChart from '@components/common/BaseChart';
import type { EChartsType } from 'echarts/lib/echarts';
import { humanize } from '@utils/humanizeUtil';
import { useFlatTreeData } from './hooks/useTreeData';
import PartitionList from './PartitionList';
import Breadcrumb, { type NavItem } from './Breadcrumb';
import { TreeMapWrapper } from './styled';
import type { TreeDataProps, TreeRootNode } from './hooks/useTreeData';

interface Props extends TreeDataProps {
  navItems: NavItem[];
  onDrillDown: (params: Record<string, any>) => void;
  onNavigate: (params: NavItem) => void;
  id?: string;
  title?: string;
  formatter?: (value: number, total?: number) => string;
}

function createLevel(tree: TreeRootNode[]) {
  return [
    {
      color: tree.map((node) => node.color),
      itemStyle: {
        borderWidth: 0,
        gapWidth: 3,
      },
    },
    {
      itemStyle: {
        gapWidth: 1,
      },
    },
  ];
}

const DrillDownTreemap = ({
  id = '',
  title,
  navItems,
  data,
  drillDown,
  labelField = 'name',
  valueField,
  partitionBy,
  onNavigate,
  onDrillDown,
  colorMap,
  formatter = (value: number) => humanize(value) ?? '',
  total,
}: Props) => {
  const [instance, setInstance] = React.useState<EChartsType | null>(null);
  const [legends, setLegends] = React.useState<string[]>([]);
  const { tree } = useFlatTreeData({
    data,
    labelField,
    valueField,
    partitionBy,
    colorMap,
    drillDown,
  });

  const chartInstanceRefCb: any = React.useCallback((instance) => {
    setInstance(instance);
  }, []);

  React.useEffect(() => {
    if (instance) {
      const option = {
        tooltip: {
          show: true,
          // valueFormatter: (value: number) => showPercentage ? `${humanize(value / Number(total?.[valueField]) * 100)}%` : humanize(value),
          valueFormatter: (value: number) => formatter(value, total?.[valueField]),
        },
        series: [
          {
            type: 'treemap',
            roam: false,
            nodeClick: false,
            left: 10,
            right: 80,
            name: title,
            visibleMin: 300,
            breadcrumb: {
              show: false,
            },
            itemStyle: {
              borderColor: '#fff',
            },
            levels: createLevel(tree),
            data: tree,
          },
        ],
      };
      instance.setOption(option);
      setLegends(tree.map((i) => i.name));
    }
  }, [tree, instance, title, formatter, total, valueField]);

  React.useEffect(() => {
    const onClick = (params: any) => {
      // Check if what you clicked on is part of a treemap
      if (params.seriesType === 'treemap' && params.data) {
        onDrillDown(params.data);
      }
    };
    instance?.on('click', onClick);
    return () => {
      instance?.off('click', onClick);
    };
  }, [navItems, instance, onDrillDown]);

  const onChange = React.useCallback(
    (legends: string[]) => {
      if (instance && tree) {
        const data = tree.filter((i) => legends.includes(i.name));
        const option = {
          series: [
            {
              levels: createLevel(data),
              data,
            },
          ],
        };
        instance.setOption(option);
        setLegends(legends);
      }
    },
    [tree, instance],
  );

  const onNav = React.useCallback(
    (item: NavItem) => {
      if (navItems[navItems.length - 1].key === item.key) {
        return; // do nothing
      }
      onNavigate(item);
    },
    [navItems, onNavigate],
  );

  return (
    <TreeMapWrapper>
      <BaseChart
        chartId={`treemap-${id}`}
        chartInstanceRefCb={chartInstanceRefCb}
        style={{ height: '100%' }}
      />
      <PartitionList data={tree} legends={legends} onChange={onChange} />
      <Breadcrumb data={navItems} onClick={onNav} />
    </TreeMapWrapper>
  );
};

export default React.memo(DrillDownTreemap);
